import React from 'react';
import type { ApiSequenceStep } from '@chatsdet/types';
import { ConfigField } from './ConfigField';
import {
  CredentialPersonaPicker,
  type CredentialAndPersonaOptions,
} from './CredentialPersonaPicker';

type StepAuthenticationFieldProps = {
  step: ApiSequenceStep;
  credentialPickerOptions: CredentialAndPersonaOptions;
  onUpdate: (step: ApiSequenceStep) => void;
};

export function StepAuthenticationField({
  step,
  credentialPickerOptions,
  onUpdate,
}: StepAuthenticationFieldProps) {
  const handlePick = React.useCallback(
    (id: string | null) => {
      if (id === null) {
        onUpdate({
          ...step,
          credentialId: null,
          credentialTemplateId: null,
          credentialTemplateName: null,
        });
        return;
      }

      if (credentialPickerOptions.templateIdSet.has(id)) {
        onUpdate({
          ...step,
          credentialTemplateId: id,
          credentialTemplateName: credentialPickerOptions.templateNameById.get(id) ?? null,
          credentialId: null,
        });
        return;
      }

      onUpdate({
        ...step,
        credentialId: id,
        credentialTemplateId: null,
        credentialTemplateName: null,
      });
    },
    [credentialPickerOptions.templateIdSet, credentialPickerOptions.templateNameById, onUpdate, step]
  );

  return (
    <ConfigField
      label="Authentication"
      description="Which credential or persona authenticates this request. A persona resolves to the right credential per environment and logs in on demand; its cookies and tokens are sent automatically."
    >
      <CredentialPersonaPicker
        templates={credentialPickerOptions.templates}
        credentials={credentialPickerOptions.credentials}
        propertyNameById={credentialPickerOptions.propertyNameById}
        templateNameById={credentialPickerOptions.templateNameById}
        isLoading={credentialPickerOptions.isLoading}
        allowNoCredential
        label={null}
        ariaLabel="Credential"
        selectedTemplateId={step.credentialTemplateId ?? null}
        selectedCredentialId={step.credentialId ?? null}
        onPick={handlePick}
      />
    </ConfigField>
  );
}
